import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { unwrap } from '@/api/envelope';
import { invitePortalUser, type OrganizationRole } from '@/api/generated';
import { applyServerErrors } from '@/api/serverErrors';
import { useReadOnly } from '@/auth/useViewingAs';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Field } from '@/components/ui/Field';
import { useToast } from '@/components/ui/useToast';
import { ROLE_LABELS } from '@/lib/portalUsers';

import { usersKey } from './keys';
import { ROLE_SUMMARIES, ROLE_ORDER } from './roles';

const inviteSchema = z.object({
  full_name: z.string().trim().min(1, 'Enter their name'),
  email: z
    .string()
    .trim()
    .min(1, 'Enter their email address')
    .email('Enter a valid email address'),
  // Empty until chosen, so a Super Admin decides rather than accepting
  // whichever role happened to be ticked already.
  role: z.enum(['ADMIN', 'SUPER_ADMIN'], { message: 'Choose a role' }),
});

type InviteInput = z.input<typeof inviteSchema>;
type InviteValues = z.output<typeof inviteSchema>;

/** The server's field names to the form's, in page order. They match. */
const SERVER_FIELDS = {
  full_name: 'full_name',
  email: 'email',
  role: 'role',
} as const;

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Invite somebody to use the portal for this organization.
 *
 * A form in the controlled dialog: it stays open, with everything typed still
 * there, when the invitation fails, and closes when it succeeds. Only this
 * component knows which happened, so it owns the request.
 *
 * The page mounts a fresh one each time it opens, so nothing from the last
 * invitation is ever on screen.
 */
export function InviteUserDialog({ open: asked, onClose }: InviteUserDialogProps) {
  // Never opens while staff view the organization, whatever asked it to.
  const readOnly = useReadOnly();
  const open = asked && !readOnly;
  const queryClient = useQueryClient();
  const toast = useToast();

  const id = useId();
  const formId = `${id}-form`;
  const roleLabelId = `${id}-role`;
  const roleErrorId = `${id}-role-error`;

  const [failure, setFailure] = useState<string | null>(null);
  // True while "Leave without saving?" is open above this dialog.
  const [confirmingClose, setConfirmingClose] = useState(false);
  const alertRef = useRef<HTMLDivElement>(null);

  const {
    register,
    control,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isDirty },
  } = useForm<InviteInput, unknown, InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { full_name: '', email: '', role: '' as OrganizationRole },
  });

  /**
   * Closing is not a navigation, so the router blocker cannot guard it. Every
   * way out - Cancel, Escape, the backdrop - comes through here instead, and
   * typed input holds the close until the person answers. A successful
   * invitation bypasses this: it calls `onClose` itself, because sent input is
   * not unsaved.
   */
  const askToClose = () => {
    if (isDirty) setConfirmingClose(true);
    else onClose();
  };

  // Moved to, so a screen reader announces it and the person does not have to
  // go looking for why nothing happened.
  useEffect(() => {
    if (failure) alertRef.current?.focus();
  }, [failure]);

  const invite = useMutation({
    mutationFn: async (values: InviteValues) => {
      const reply = await invitePortalUser({
        body: { full_name: values.full_name, email: values.email, role: values.role },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFailure(null),
    onSuccess: async (data, values) => {
      onClose();
      // The backend says whether the email went. An invitation that exists
      // but was never emailed is the one case where "sent" would be untrue,
      // and the fix is Resend on the row, not a second invitation.
      if (data?.email_sent === false) {
        toast.show({
          message:
            'Invitation saved, but the email could not be sent. Use Resend on their row.',
          severity: 'error',
        });
      } else {
        toast.show({
          message: data?.joined_existing_account
            ? `Invitation sent to ${values.full_name}. They already have a portal account and will not need a new password.`
            : `Invitation sent to ${values.full_name}.`,
        });
      }
      await queryClient.invalidateQueries({ queryKey: usersKey });
    },
    onError: (error) => {
      // A conflict is about the address - already a member here, or one of
      // Activist's own - so it sits on the address field, in the backend's
      // words, which say which of the two it is. Any other field message
      // lands on its field; the rest goes to the alert at the top.
      const { remainder } = applyServerErrors(
        error,
        { setError, setFocus },
        SERVER_FIELDS,
        {
          conflictField: 'email',
        },
      );
      if (remainder) setFailure(remainder);
    },
  });

  return (
    <Dialog
      open={open}
      onClose={askToClose}
      title="Invite user"
      description="They will get an email with a link to set a password and sign in."
      actions={
        <>
          <Button variant="secondary" onClick={askToClose} disabled={invite.isPending}>
            Cancel
          </Button>
          {/* Outside the form element, so it names the form it submits. */}
          <Button type="submit" form={formId} loading={invite.isPending}>
            Send invitation
          </Button>
        </>
      }
    >
      <Stack
        component="form"
        id={formId}
        spacing={2.5}
        aria-label="Invite user"
        // The browser's own bubbles would pre-empt the messages this form
        // attaches to each field.
        noValidate
        onSubmit={(event) => {
          void handleSubmit((values) => invite.mutate(values))(event);
        }}
        sx={{ pt: 0.5 }}
      >
        {failure ? (
          <Alert severity="error" role="alert" ref={alertRef} tabIndex={-1}>
            {failure}
          </Alert>
        ) : null}

        <Field label="Full name" error={errors.full_name?.message}>
          <TextField {...register('full_name')} size="small" fullWidth />
        </Field>

        <Field label="Email address" error={errors.email?.message}>
          <TextField {...register('email')} type="email" size="small" fullWidth />
        </Field>

        <FormControl error={Boolean(errors.role)}>
          <FormLabel id={roleLabelId}>Role</FormLabel>
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                aria-labelledby={roleLabelId}
                aria-describedby={errors.role ? roleErrorId : undefined}
              >
                {ROLE_ORDER.map((role) => (
                  <FormControlLabel
                    key={role}
                    value={role}
                    control={<Radio />}
                    sx={{ alignItems: 'flex-start', mt: 1 }}
                    label={
                      <>
                        <Typography component="span" sx={{ display: 'block', pt: 1 }}>
                          {ROLE_LABELS[role]}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {ROLE_SUMMARIES[role]}
                        </Typography>
                      </>
                    }
                  />
                ))}
              </RadioGroup>
            )}
          />
          {errors.role ? (
            <FormHelperText id={roleErrorId} role="alert" sx={{ fontWeight: 500 }}>
              {errors.role.message}
            </FormHelperText>
          ) : null}
        </FormControl>
      </Stack>

      {/* Above this dialog. Stay puts focus back where it was in here. */}
      <UnsavedChangesDialog
        blocked={confirmingClose}
        stay={() => setConfirmingClose(false)}
        leave={() => {
          setConfirmingClose(false);
          onClose();
        }}
      />
    </Dialog>
  );
}
